import { useState } from 'react';
import { Plus, Clock, Calendar, User } from 'lucide-react';
import { useProjectTimeEntries } from '@/hooks/useProjects';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

interface ProjectTimeEntriesProps {
  projectId: string;
  limit?: number;
}

export default function ProjectTimeEntries({ projectId, limit }: ProjectTimeEntriesProps) {
  const { data: timeEntries, isLoading } = useProjectTimeEntries(projectId);
  
  const displayedEntries = limit ? timeEntries?.slice(0, limit) : timeEntries;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'overtime':
        return 'bg-orange-100 text-orange-800';
      case 'meeting':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-gray-100 text-gray-800';
      case 'research':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  const totalHours = displayedEntries?.reduce((sum, entry) => sum + (entry.hours_logged || 0), 0) || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">
            {limit ? 'Recent Time Entries' : 'Time Tracking'}
          </h3>
          {!limit && totalHours > 0 && (
            <p className="text-sm text-muted-foreground">
              Total: {totalHours.toFixed(1)} hours logged
            </p>
          )}
        </div>
        {!limit && (
          <Button size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Log Time
          </Button>
        )}
      </div>

      {displayedEntries && displayedEntries.length > 0 ? (
        <div className="space-y-3">
          {displayedEntries.map((entry) => (
            <div key={entry.id} className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-start gap-3">
                <Clock className="h-4 w-4 text-muted-foreground mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{entry.task_description}</h4>
                    <Badge className={getTypeColor(entry.time_entry_type)}>
                      {entry.time_entry_type.replace('_', ' ')}
                    </Badge>
                    {entry.billable && (
                      <Badge variant="outline">Billable</Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(entry.entry_date), 'MMM d, yyyy')}
                    </div>
                    
                    {entry.start_time && entry.end_time && (
                      <span>{entry.start_time} - {entry.end_time}</span>
                    )}
                    
                    {entry.user && (
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {entry.user.first_name} {entry.user.last_name}
                      </div>
                    )}
                  </div>
                  
                  {entry.notes && (
                    <p className="text-sm text-muted-foreground mt-1">{entry.notes}</p>
                  )}
                </div>
              </div>
              
              <div className="text-right">
                <div className="font-medium">{entry.hours_logged}h</div>
                <Badge className={getStatusColor(entry.approval_status)}>
                  {entry.approval_status}
                </Badge>
              </div>
            </div>
          ))}
          
          {limit && timeEntries && timeEntries.length > limit && (
            <div className="text-center">
              <Button variant="link" className="text-sm">
                View all {timeEntries.length} time entries →
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Time Entries</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Start logging time to track project progress and profitability.
          </p>
          {!limit && (
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Log First Entry
            </Button>
          )}
        </div>
      )}
    </div>
  );
}